import { useEffect, useRef, useState } from "react";
import { ChevronDown, Building2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectFilter } from "@/lib/ProjectFilterContext";

/**
 * Compact global project filter pill. Renders in every PageShell header.
 * Fetches projects once, shares them through ProjectFilterContext so downstream
 * pages can consume without re-fetching.
 */
export default function ProjectFilterPill() {
  const { project, setProject, projects, setProjects } = useProjectFilter();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (projects.length === 0) {
      api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const label = project === "all" ? "All Projects" : project;
  const filtered = search
    ? projects.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
    : projects;

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="global-project-filter"
        onClick={() => setOpen((o) => !o)}
        className="cf-touch inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-surface text-ink text-xs md:text-sm font-medium hover:bg-surface-2 transition-colors border border-hairline shadow-sm"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Building2 size={13} className="text-gold-600" />
        <span className="max-w-[160px] truncate">{label}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="global-project-filter-menu"
          className="absolute right-0 mt-2 w-64 max-h-80 overflow-y-auto rounded-xl bg-surface shadow-xl border border-hairline z-50"
          role="listbox"
        >
          <div className="p-2 border-b border-hairline/50 sticky top-0 bg-surface z-10">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search projects…"
              className="w-full px-2 py-1.5 text-sm bg-paper rounded focus:outline-none"
              autoFocus
            />
          </div>
          <button
            data-testid="global-project-filter-all"
            onClick={() => { setProject("all"); setOpen(false); }}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-2 flex items-center justify-between ${project === "all" ? "font-semibold text-ink" : "text-muted-dark"}`}
          >
            <span>All Projects</span>
            {project === "all" && <Check size={13} className="text-gold-600" />}
          </button>
          <div className="border-t border-hairline/50" />
          {filtered.map((p) => (
            <button
              key={p.id}
              data-testid={`global-project-filter-opt-${p.name}`}
              onClick={() => { setProject(p.name); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-2 flex items-center justify-between ${project === p.name ? "font-semibold text-ink" : "text-muted-dark"}`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${p.type === "new" ? "bg-gold-500" : p.type === "ongoing" ? "bg-ink" : "bg-[#7BB3F0]"}`} />
                <span className="truncate">{p.name}</span>
                <span className="text-[9px] uppercase tracking-widest text-muted-light">{p.type}</span>
              </span>
              {project === p.name && <Check size={13} className="text-gold-600" />}
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-xs text-muted-light text-center">No projects match.</div>
          )}
        </div>
      )}
    </div>
  );
}
